import {
    AfterViewInit,
    Component,
    effect,
    inject,
    input,
    OnDestroy,
    OnInit,
    untracked,
    viewChild,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { UntilDestroy } from '@ngneat/until-destroy';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { FormControl, FormGroup } from '@angular/forms';
import { EcmrStatus } from '@app/shared/models/ecmr/EcmrStatus';
import { EcmrTransportType } from '@app/shared/models/ecmr/EcmrTransportType';
import { CdkDrag, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DateFormatService } from '@app/shared/services/date-format.service';
import { EcmrService } from '@app/core/services/ecmr-services/ecmr.service';
import { ShowColumns } from '@app/shared/models/ecmr/show-columns';
import { Ecmr } from '@app/shared/models/ecmr/Ecmr';
import { FilterRequest } from '@app/shared/models/ecmr/filter-request';
import { EcmrType } from '@app/shared/models/ecmr/EcmrType';
import { lastValueFrom, Subject } from 'rxjs';
import { EcmrPage } from '@app/shared/models/ecmr/EcmrPage';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { EcmrDetailDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-detail-dialog/ecmr-detail-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { EcmrShareDialogComponent } from '@app/shared/components/ecmr/dialogs/ecmr-share-dialog/ecmr-share-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { PdfExtractionStatus } from '@app/shared/models/ecmr/PdfExtractionStatus';
import { SearchStore } from '@app/stores/searchStore';
import { AppStore } from '@app/stores/appStore';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/shared/material/material.module';
import { TranslateModule } from '@ngx-translate/core';
import { ReactiveFormsModule } from '@angular/forms';
import { EcmrStatusComponent } from '@app/shared/components/ecmr/subcomponents/ecmr-status/ecmr-status.component';
import { LatestMotorPlatePipe } from '@app/shared/pipes/latest-motor-plate.pipe';
import { isEcmrDcaCompliant } from '@app/shared/components/ecmr/ecmr-dca.util';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-active-ecmr',
    templateUrl: './active-ecmr.component.html',
    styleUrl: './active-ecmr.component.scss',
    imports: [
        CommonModule,
        MaterialModule,
        TranslateModule,
        ReactiveFormsModule,
        EcmrStatusComponent,
        LatestMotorPlatePipe,
    ],
})
export class ActiveEcmrComponent implements OnInit, AfterViewInit, OnDestroy {
    baseRoute = input<string>();
    shouldReloadTable = input<Subject<boolean>>();
    isReadOnly = input<boolean>(false);

    private appStore = inject(AppStore);
    private searchStore = inject(SearchStore);

    isLoading: boolean = false;
    organizationSiteKey = this.appStore.currentOrganizationSiteKey;

    dataSource: MatTableDataSource<Ecmr> = new MatTableDataSource<Ecmr>();

    intervalId: ReturnType<typeof setInterval>;
    private paginatorReady = false;
    private pendingSearchQuery: string | null = null;

    paginator = viewChild(MatPaginator);
    sort = viewChild(MatSort);

    displayedColumns: string[] = [
        'id',
        'referenceId',
        'from',
        'to',
        'transportType',
        'lastEditor',
        'status',
        'lastEditDate',
        'creationDate',
        'licensePlate',
        'carrierName',
        'carrierPostCode',
        'consigneePostCode',
    ];
    columns: string[] = [
        'id',
        'referenceId',
        'from',
        'to',
        'transportType',
        'lastEditor',
        'status',
        'lastEditDate',
        'creationDate',
        'licensePlate',
        'carrierName',
        'carrierPostCode',
        'consigneePostCode',
    ];
    filteredColumns: string[] = [];

    // toggle for display of columns
    showColumns: ShowColumns = {
        id: true,
        referenceId: true,
        from: true,
        to: true,
        transportType: true,
        lastEditor: true,
        status: true,
        lastEditDate: true,
        creationDate: true,
        licensePlate: false,
        carrierName: false,
        carrierPostCode: false,
        consigneePostCode: false,
    };

    // toggle for filter selection
    showColumSelection: boolean = false;
    toggledColumns = [
        this.showColumns.referenceId,
        this.showColumns.from,
        this.showColumns.to,
        this.showColumns.transportType,
        this.showColumns.lastEditor,
        this.showColumns.status,
        this.showColumns.lastEditDate,
        this.showColumns.creationDate,
    ];

    filterRequest: FilterRequest = {
        ecmrId: null,
        referenceId: '',
        from: '',
        to: '',
        transportType: null,
        status: null,
        licensePlate: '',
        carrierName: '',
        carrierPostCode: '',
        consigneePostCode: '',
        lastEditor: '',
        createdAt: '',
        editedAt: '',
        groupId: null,
        transportId: null,
    };

    filterFormGroup = new FormGroup({
        referenceId: new FormControl<string | null>(null),
        from: new FormControl<string | null>(null),
        to: new FormControl<string | null>(null),
        transportType: new FormControl<EcmrTransportType | null>(null),
        status: new FormControl<EcmrStatus | null>(null),
        licensePlate: new FormControl<string | null>(null),
        carrierName: new FormControl<string | null>(null),
        carrierPostCode: new FormControl<string | null>(null),
        consigneePostCode: new FormControl<string | null>(null),
        lastEditor: new FormControl<string | null>(null),
    });

    protected readonly EcmrStatus = EcmrStatus;
    protected readonly EcmrTransportType = EcmrTransportType;

    constructor(
        private ecmrService: EcmrService,
        protected dateFormatService: DateFormatService,
        private sessionService: SessionService,
        private router: Router,
        private toastr: ToastrService,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {
        // Listen to search state changes from the store
        effect(() => {
            const query = this.searchStore.searchText();
            this.applySearchFilter(query);
        });

        // Listen to organization site changes from the store
        effect(() => {
            const orgSiteKey = this.organizationSiteKey();
            if (orgSiteKey) {
                untracked(() => {
                    this.startRefreshInterval();
                });
            }
        });

        // Listen to refresh trigger
        effect(() => {
            const orgSiteKey = this.organizationSiteKey();
            if (orgSiteKey) {
                const refreshCount = this.searchStore.refreshTrigger();
                if (refreshCount > 0) {
                    untracked(() => {
                        this.loadEcmrs(orgSiteKey);
                    });
                }
            }
        });
    }

    ngOnInit() {
        this.initColumns();
    }

    async ngAfterViewInit() {
        // Set default sort to newest creation date first
        const sort = this.sort();
        if (sort) {
            sort.active = 'creationDate';
            sort.direction = 'desc';
        }

        this.paginatorReady = true;

        // Apply any pending search query, otherwise do the initial load now
        // that the paginator's [pageSize] input binding has been applied
        // (before this, paginator.pageSize is undefined). A constructor
        // effect may have already tried to load and been blocked by the
        // paginatorReady guard, so we kick it off here.
        if (this.pendingSearchQuery !== null) {
            this.applySearchFilter(this.pendingSearchQuery);
            this.pendingSearchQuery = null;
        } else {
            const siteKey = this.sessionService.currentOrganizationSiteKey();
            if (siteKey) {
                this.loadEcmrs(siteKey);
            }
        }

        this.shouldReloadTable()?.subscribe(result => {
            if (result) {
                this.loadEcmrs(
                    this.sessionService.currentOrganizationSiteKey()
                );
            }
        });
    }

    ngOnDestroy() {
        clearInterval(this.intervalId);
    }

    async loadEcmrs(siteKey: string) {
        // Guard: Don't load until the view (and the paginator's [pageSize]
        // input binding) is ready, otherwise paginator.pageSize is still
        // undefined and would be sent as size=undefined.
        const paginator = this.paginator();
        const sort = this.sort();
        if (!this.paginatorReady || !paginator || !sort) {
            return;
        }

        this.isLoading = true;

        try {
            const page: EcmrPage = await lastValueFrom(
                this.ecmrService.getAllEcmr(
                    this.filterRequest,
                    siteKey,
                    EcmrType.ECMR,
                    paginator.pageIndex,
                    paginator.pageSize,
                    sort.active || 'creationDate',
                    (sort.direction || 'desc').toUpperCase()
                )
            );

            this.dataSource.data = page.ecmrs;
            paginator.length = page.totalElements;
        } catch (e) {
            console.error('eCMR loading error:', e);
            this.toastr.error(
                this.translateService.instant('ECMR.error.loading_ecmr_failed')
            );
        } finally {
            this.isLoading = false;
        }
    }

    applySearchFilter(query: string) {
        // Store query for later if paginator not ready yet
        if (!this.paginatorReady) {
            this.pendingSearchQuery = query;
            return;
        }

        this.filterRequest.referenceId = query;
        this.filterRequest.from = query;
        this.filterRequest.to = query;
        this.filterRequest.carrierName = query;
        this.filterRequest.carrierPostCode = query;
        this.filterRequest.licensePlate = query;
        this.filterRequest.lastEditor = query;
        this.filterRequest.consigneePostCode = query;
        this.filterRequest.createdAt = query;
        this.filterRequest.editedAt = query;

        if (query) {
            const enumQuery = query.trim().toLowerCase();
            this.filterRequest.status =
                Object.values(EcmrStatus).find(status => {
                    const translated = this.translateService
                        .instant('ECMR.COMMON.STATUS.' + status)
                        .toLowerCase();
                    return enumQuery !== '' && translated.startsWith(enumQuery);
                }) || null;

            this.filterRequest.transportType =
                Object.values(EcmrTransportType).find(
                    type =>
                        enumQuery !== '' &&
                        type.toLowerCase().startsWith(enumQuery)
                ) || null;
        } else {
            this.filterRequest.status = null;
            this.filterRequest.transportType = null;
        }

        this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
    }

    async startRefreshInterval() {
        await this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        clearInterval(this.intervalId);
        this.intervalId = setInterval(() => {
            this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        }, 30 * 1000);
    }

    initColumns() {
        //Check for saved column configuration
        const savedShowColumns: ShowColumns | null =
            this.ecmrService.getShowColumns();
        if (savedShowColumns) this.showColumns = savedShowColumns;

        //Check for saved column order configuration
        const savedColumns: string[] | null =
            this.ecmrService.getDisplayedColumns();
        if (savedColumns) {
            this.columns = savedColumns;
        }
        this.updateDisplayedColumns();
    }

    updateDisplayedColumns() {
        this.displayedColumns = this.columns.filter(
            column => this.showColumns[column as keyof ShowColumns]
        );
    }

    sortAndUpdateData() {
        this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
    }

    sortPredicate(index: number, item: CdkDrag<number>) {
        return index != 0;
    }

    signEcmr(ecmrId: string) {
        if (ecmrId) {
            this.router.navigateByUrl(
                this.baseRoute() +
                    '/' +
                    RoutingEndpoints.ecmr_editor +
                    `/${ecmrId}?action=sign`
            );
            /*this.router.navigate([
                this.baseRoute +
                    '/' +
                    RoutingEndpoints.ecmr_editor +
                    `/${ecmrId}?action=sign`,
            ]);*/
        }
    }

    editEcmr(ecmrId: string) {
        if (ecmrId) {
            this.router.navigate([
                this.baseRoute() +
                    '/' +
                    RoutingEndpoints.ecmr_editor +
                    `/${ecmrId}`,
            ]);
        }
    }

    async deleteEcmr(ecmrId: string) {
        try {
            await lastValueFrom(
                this.ecmrService.deleteEcmr(
                    ecmrId,
                    this.sessionService.currentOrganizationSiteKey()
                )
            );
            this.toastr.success(
                this.translateService.instant('ECMR.overview.delete_success')
            );
            this.loadEcmrs(this.sessionService.currentOrganizationSiteKey());
        } catch (e) {
            this.toastr.error(
                this.translateService.instant('ECMR.error.ecmr_delete_failed')
            );
        }
    }

    isDeleteButtonVisible(ecmr: Ecmr) {
        return ecmr.ecmrStatus === EcmrStatus.NEW;
    }

    /**
     * DeCA badge shows once the sender has signed - the backend leaves the
     * status at NEW until then - and the tax numbers make the consignment
     * conform.
     */
    isDcaCompliant(ecmr: Ecmr): boolean {
        return (
            ecmr.ecmrStatus !== EcmrStatus.NEW &&
            isEcmrDcaCompliant(ecmr.ecmrConsignment)
        );
    }

    shareEcmr(ecmr: Ecmr) {
        if (ecmr?.ecmrId) {
            const dialogRef = this.dialog.open(EcmrShareDialogComponent, {
                width: '75vw',
                data: {
                    ecmr: ecmr,
                },
            });
        }
    }

    //DragDrop function to move and save columns
    tableDrop(event: CdkDragDrop<string[]>) {
        const movedColumn = this.displayedColumns[event.previousIndex];
        const actualPreviousIndex = this.columns.indexOf(movedColumn);
        let actualCurrentIndex = event.currentIndex;
        if (actualCurrentIndex >= this.columns.length) {
            actualCurrentIndex = this.columns.length - 1;
        }
        if (actualPreviousIndex > 0 && actualCurrentIndex > 0) {
            moveItemInArray(
                this.columns,
                actualPreviousIndex,
                actualCurrentIndex
            );
        }
        moveItemInArray(
            this.displayedColumns,
            event.previousIndex,
            event.currentIndex
        );
        this.ecmrService.saveDisplayedColumns(this.columns);
    }

    /**
     * Toggle the {@link #showColumSelection} to show filter selection menu.
     */
    toggleColumnSelectionMenu() {
        this.showColumSelection = !this.showColumSelection;
    }

    toggleColumnAtIndex(index: number) {
        return (this.toggledColumns[index] = !this.toggledColumns[index]);
    }

    toggleColumnAtKey(key: string) {
        this.showColumns[key as keyof ShowColumns] =
            !this.showColumns[key as keyof ShowColumns];
    }

    closeColumnSelection() {
        const savedShowColumns: ShowColumns | null =
            this.ecmrService.getShowColumns();
        if (savedShowColumns) this.showColumns = savedShowColumns;
        this.showColumSelection = false;
    }

    /**
     * Update the table's columns and only show active columns
     */
    updateColumns() {
        this.displayedColumns = [];
        this.columns.forEach(column => {
            if (this.showColumns[column as keyof ShowColumns]) {
                this.displayedColumns.push(column);
            }
        });
        this.ecmrService.saveShowColumns(this.showColumns);

        this.showColumSelection = false;
    }

    public getTransportType(ecmr: Ecmr): EcmrTransportType | null {
        return this.ecmrService.getTransportType(ecmr);
    }

    showDetailsForEcmrAtIndex(index: number) {
        const dialogRef = this.dialog.open(EcmrDetailDialogComponent, {
            width: '80vw',
            height: '80vh',
            disableClose: false,
            data: {
                baseRoute: this.baseRoute(),
                ecmr: this.dataSource.data[index],
                isArchive: false,
                isReadOnly: this.isReadOnly(),
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.loadEcmrs(
                    this.sessionService.currentOrganizationSiteKey()
                );
            }
        });
    }

    protected readonly PdfExtractionStatus = PdfExtractionStatus;
}
